import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { divIcon } from 'leaflet';
import { MapContainer, Marker, Rectangle, TileLayer, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import { getExpeditionTileStatus, loadExpeditionState, loadExpeditionTiles, scanExpeditionArea } from '../api/expeditionsApi';
import { loadShipyard } from '../api/shipyardApi';
import { useGamification } from '../hooks/GamificationContext';
import { useOptionalAppSession } from '../hooks/AppSessionContext';
import { usePlayerLocation } from '../game-map/PlayerLocationContext';

const LIVE_POSITION_MIN_DISTANCE_METERS = 8;
const EXPEDITION_MAP_MIN_ZOOM = 8;
const EXPEDITION_DETAIL_MIN_ZOOM = 14;
const EXPEDITION_MAX_DETAIL_TILES = 900;
const EXPEDITION_VIEWPORT_TILE_LIMIT = 3000;

const DEFAULT_SHIP_MARKER_IMAGE = 'https://app.pfandpirat.com/wp-content/uploads/pfandpirat.png';

const SHIP_MARKER_IMAGES = {
  1: 'https://app.pfandpirat.com/wp-content/uploads/floss-klein.png',
  2: 'https://app.pfandpirat.com/wp-content/uploads/hafenjolle-klein.png',
  3: 'https://app.pfandpirat.com/wp-content/uploads/piratenschaluppe-klein.png',
  4: 'https://app.pfandpirat.com/wp-content/uploads/freibeuter-kutter-klein.png',
  5: 'https://app.pfandpirat.com/wp-content/uploads/goldene-pfandgaleone-klein.png',
  floss: 'https://app.pfandpirat.com/wp-content/uploads/floss-klein.png',
  floß: 'https://app.pfandpirat.com/wp-content/uploads/floss-klein.png',
  treibholzfloss: 'https://app.pfandpirat.com/wp-content/uploads/floss-klein.png',
  treibholzfloß: 'https://app.pfandpirat.com/wp-content/uploads/floss-klein.png',
  hafenjolle: 'https://app.pfandpirat.com/wp-content/uploads/hafenjolle-klein.png',
  piratenschaluppe: 'https://app.pfandpirat.com/wp-content/uploads/piratenschaluppe-klein.png',
  freibeuterkutter: 'https://app.pfandpirat.com/wp-content/uploads/freibeuter-kutter-klein.png',
  goldenepfandgaleone: 'https://app.pfandpirat.com/wp-content/uploads/goldene-pfandgaleone-klein.png',
};

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function normalizeCoord(value) {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
    return null;
  }

  return Math.round(Number(value) * 10000000) / 10000000;
}

function expeditionTileKey(latitude, longitude) {
  const lat = normalizeCoord(latitude);
  const lng = normalizeCoord(longitude);

  if (lat === null || lng === null) return '';

  const latKey = Math.floor((lat + 90) * 1000);
  const lngKey = Math.floor((lng + 180) * 1000);

  return `t${latKey}_${lngKey}`;
}

function tileBoundsFromKey(tileKey) {
  const match = String(tileKey || '').match(/^t(-?\d+)_(-?\d+)$/);
  if (!match) return null;

  const latKey = Number(match[1]);
  const lngKey = Number(match[2]);
  const south = (latKey / 1000) - 90;
  const west = (lngKey / 1000) - 180;
  const north = ((latKey + 1) / 1000) - 90;
  const east = ((lngKey + 1) / 1000) - 180;

  return [[south, west], [north, east]];
}

function tileBoundsFromPayload(tile) {
  const bounds = tile?.bounds || null;
  if (
    bounds
    && [bounds.south, bounds.west, bounds.north, bounds.east].every((value) => Number.isFinite(Number(value)))
  ) {
    return [[Number(bounds.south), Number(bounds.west)], [Number(bounds.north), Number(bounds.east)]];
  }

  return tileBoundsFromKey(tile?.tile_key);
}

function tileKeyParts(tileKey) {
  const match = String(tileKey || '').match(/^t(-?\d+)_(-?\d+)$/);
  if (!match) return null;

  return {
    latKey: Number(match[1]),
    lngKey: Number(match[2]),
  };
}

function nearbyTileKeys(center, radius = 4) {
  const centerKey = expeditionTileKey(center?.latitude, center?.longitude);
  const parts = tileKeyParts(centerKey);
  if (!parts) return [];

  const keys = [];

  for (let lat = parts.latKey - radius; lat <= parts.latKey + radius; lat += 1) {
    for (let lng = parts.lngKey - radius; lng <= parts.lngKey + radius; lng += 1) {
      keys.push(`t${lat}_${lng}`);
    }
  }

  return keys;
}

function bboxFromMapBounds(bounds) {
  if (!bounds || typeof bounds.getSouth !== 'function') {
    return null;
  }

  const south = Math.max(-90, normalizeCoord(bounds.getSouth()) ?? -90);
  const north = Math.min(90, normalizeCoord(bounds.getNorth()) ?? 90);
  const west = Math.max(-180, normalizeCoord(bounds.getWest()) ?? -180);
  const east = Math.min(180, normalizeCoord(bounds.getEast()) ?? 180);

  return {
    south: Math.min(south, north),
    west: Math.min(west, east),
    north: Math.max(south, north),
    east: Math.max(west, east),
  };
}

function tileKeysFromMapBounds(bounds, fallbackCenter, zoom = 15) {
  if (!bounds || typeof bounds.getSouth !== 'function' || Number(zoom) < EXPEDITION_DETAIL_MIN_ZOOM) {
    return nearbyTileKeys(fallbackCenter, 4);
  }

  const bbox = bboxFromMapBounds(bounds);
  if (!bbox) {
    return nearbyTileKeys(fallbackCenter, 6);
  }

  const south = bbox.south;
  const north = bbox.north;
  const west = bbox.west;
  const east = bbox.east;
  const startLatKey = Math.floor((Math.min(south, north) + 90) * 1000) - 1;
  const endLatKey = Math.floor((Math.max(south, north) + 90) * 1000) + 1;
  const startLngKey = Math.floor((Math.min(west, east) + 180) * 1000) - 1;
  const endLngKey = Math.floor((Math.max(west, east) + 180) * 1000) + 1;
  const tileCount = Math.max(0, endLatKey - startLatKey + 1) * Math.max(0, endLngKey - startLngKey + 1);

  if (tileCount > EXPEDITION_MAX_DETAIL_TILES) {
    return nearbyTileKeys(fallbackCenter, 6);
  }

  const keys = [];

  for (let lat = startLatKey; lat <= endLatKey; lat += 1) {
    for (let lng = startLngKey; lng <= endLngKey; lng += 1) {
      keys.push(`t${lat}_${lng}`);
    }
  }

  return keys;
}

function expeditionViewportFromMap(map, center) {
  const zoom = typeof map?.getZoom === 'function' ? map.getZoom() : 15;
  const bounds = typeof map?.getBounds === 'function' ? map.getBounds() : null;
  const bbox = bboxFromMapBounds(bounds);

  return {
    bbox,
    zoom,
    showHiddenGrid: Number(zoom) >= EXPEDITION_DETAIL_MIN_ZOOM,
    tileKeys: tileKeysFromMapBounds(bounds, center, zoom),
  };
}

function viewportCacheKey(viewport) {
  const bbox = viewport?.bbox;
  if (!bbox) return '';

  return [
    Math.round(Number(viewport?.zoom || 0)),
    Number(bbox.south).toFixed(4),
    Number(bbox.west).toFixed(4),
    Number(bbox.north).toFixed(4),
    Number(bbox.east).toFixed(4),
  ].join(':');
}

function mergeExpeditionTiles(existing = [], incoming = []) {
  const byKey = new Map();
  (Array.isArray(existing) ? existing : []).forEach((tile) => {
    if (tile?.tile_key) byKey.set(tile.tile_key, tile);
  });
  (Array.isArray(incoming) ? incoming : []).forEach((tile) => {
    if (tile?.tile_key) byKey.set(tile.tile_key, tile);
  });

  return Array.from(byKey.values());
}

function hasCoords(position) {
  return normalizeCoord(position?.latitude) !== null && normalizeCoord(position?.longitude) !== null;
}

function distanceMeters(a, b) {
  if (!hasCoords(a) || !hasCoords(b)) return null;

  const earthRadius = 6371000;
  const lat1 = Number(a.latitude) * Math.PI / 180;
  const lat2 = Number(b.latitude) * Math.PI / 180;
  const deltaLat = (Number(b.latitude) - Number(a.latitude)) * Math.PI / 180;
  const deltaLng = (Number(b.longitude) - Number(a.longitude)) * Math.PI / 180;
  const sinLat = Math.sin(deltaLat / 2);
  const sinLng = Math.sin(deltaLng / 2);
  const value = Math.min(1, (sinLat * sinLat) + Math.cos(lat1) * Math.cos(lat2) * (sinLng * sinLng));

  return earthRadius * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}

function escapeMarkerAttribute(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function normalizeShipKey(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9äöüß]/g, '');
}

function getShipMarkerImage(ship) {
  const level = Number(ship?.level || 0);
  const nameKey = normalizeShipKey(ship?.name);
  const slugKey = normalizeShipKey(ship?.slug);

  return SHIP_MARKER_IMAGES[nameKey]
    || SHIP_MARKER_IMAGES[slugKey]
    || SHIP_MARKER_IMAGES[level]
    || DEFAULT_SHIP_MARKER_IMAGE;
}

function MapViewportSync({ center }) {
  const map = useMap();

  useEffect(() => {
    if (!hasCoords(center)) return;
    map.setView([center.latitude, center.longitude], map.getZoom() || 15, { animate: true });
  }, [center?.latitude, center?.longitude, map]);

  return null;
}

function MapGridSync({ center, onViewportChange }) {
  const refresh = useCallback((map) => {
    if (typeof onViewportChange === 'function') {
      onViewportChange(expeditionViewportFromMap(map, center));
    }
  }, [center?.latitude, center?.longitude, onViewportChange]);

  const map = useMapEvents({
    moveend(event) {
      refresh(event.target);
    },
    resize(event) {
      refresh(event.target);
    },
    zoomend(event) {
      refresh(event.target);
    },
  });

  useEffect(() => {
    refresh(map);
    const timer = window.setTimeout(() => refresh(map), 160);
    return () => window.clearTimeout(timer);
  }, [map, refresh]);

  return null;
}

function getCooldownLeftSeconds(state = {}, nowMs = Date.now()) {
  const cooldownUntil = state.cooldown_until ? new Date(state.cooldown_until).getTime() : 0;

  if (Number.isFinite(cooldownUntil) && cooldownUntil > 0) {
    return Math.max(0, Math.ceil((cooldownUntil - nowMs) / 1000));
  }

  return Math.max(0, Number(state.cooldown_left_seconds || 0));
}


function formatCoinReward(value) {
  const bronzeTotal = Math.max(0, Number(value || 0));
  if (!bronzeTotal) return '';
  if (bronzeTotal % 100 === 0) {
    const gold = bronzeTotal / 100;
    return `${gold} Goldmünze${gold === 1 ? '' : 'n'}`;
  }
  if (bronzeTotal % 50 === 0) {
    const silver = bronzeTotal / 50;
    return `${silver} Silbermünze${silver === 1 ? '' : 'n'}`;
  }
  return `${bronzeTotal} Bronzemünze${bronzeTotal === 1 ? '' : 'n'}`;
}

function formatResourceRewards(resources = []) {
  if (!Array.isArray(resources) || !resources.length) return '';
  return resources
    .map((resource) => `+${Number(resource.quantity || 1)} ${resource.name || 'Werft-Ressource'}`)
    .join(' · ');
}

function rarityLabel(rarity) {  const map = {
    common: 'Häufig',
    uncommon: 'Ungewöhnlich',
    rare: 'Selten',
    epic: 'Episch',
    legendary: 'Legendär',
  };
  return map[rarity] || 'Fund';
}

function ProgressBar({ value = 0, max = 1 }) {
  const percent = Math.max(0, Math.min(100, Math.round((Number(value || 0) / Math.max(1, Number(max || 1))) * 100)));
  return (
    <div className="pfp-expedition-progress" aria-label={`${percent}%`}>
      <div style={{ width: `${percent}%` }} />
    </div>
  );
}

function LootThumb({ imageUrl, icon = '🧭', label = '' }) {
  const src = String(imageUrl || '').trim();

  if (src) {
    return <img src={src} alt={label || ''} loading="lazy" />;
  }

  return <span>{icon || '🧭'}</span>;
}

function StatCard({ icon, label, value, hint }) {
  return (
    <div className="pfp-expedition-stat-card">
      <div className="pfp-expedition-stat-icon">{icon}</div>
      <div>
        <div className="pfp-expedition-stat-value">{value}</div>
        <div className="pfp-expedition-stat-label">{label}</div>
        {hint ? <div className="pfp-expedition-stat-hint">{hint}</div> : null}
      </div>
    </div>
  );
}

function trackingLabel(status) {
  switch (status) {
    case 'watching':
      return 'Live';
    case 'pending':
      return 'Ortung...';
    case 'blocked':
      return 'GPS blockiert';
    case 'unavailable':
      return 'GPS aus';
    default:
      return 'Standort';
  }
}

function ExpeditionStartPrompt({ visible, accuracy, availableScans, onStart, onDismiss, disabled }) {
  if (!visible) return null;

  return (
    <aside className="pfp-expedition-live-toast" role="status" aria-live="polite">
      <div className="pfp-expedition-live-toast__body">
        <strong>Neues Gebiet voraus</strong>
        <span>
          Diese Kachel ist noch unentdeckt. Scanner bereit: {Number(availableScans || 0)}
          {Number(accuracy || 0) > 0 ? ` - GPS ca. ${Math.round(Number(accuracy))} m` : ''}.
        </span>
      </div>
      <div className="pfp-expedition-live-toast__actions">
        <button type="button" onClick={onStart} disabled={disabled}>Expedition starten</button>
        <button type="button" className="is-ghost" onClick={onDismiss}>Nicht jetzt</button>
      </div>
    </aside>
  );
}

function ShipCard({ shipyard }) {
  const ship = shipyard?.ship || null;
  const hasShip = ship && Number(ship.level || 0) > 0 && String(ship.name || '').trim();
  const nextName = shipyard?.next_blueprint?.name || '';
  const progress = hasShip ? Math.max(0, Math.min(100, Number(ship.progress_percent || 0))) : 0;
  const bonusLabels = hasShip && Array.isArray(ship.bonus_labels) && ship.bonus_labels.length
    ? ship.bonus_labels
    : ['Schiffsstufe verbessert deine Ressourcenfunde.'];

  return (
    <section className="pfp-expedition-card pfp-expedition-ship-card">
      <div className="pfp-expedition-ship-visual">
        {hasShip && ship.image_url ? (
          <img src={ship.image_url} alt={ship.name || 'Dein Schiff'} loading="lazy" />
        ) : (
          <div className="pfp-expedition-ship-placeholder">⚓</div>
        )}
      </div>
      <div className="pfp-expedition-ship-body">
        <span className="pfp-expedition-ship-kicker">Dein Schiff</span>
        <h2>{hasShip ? ship.name : 'Noch kein Schiff gebaut'}</h2>
        <p>{hasShip ? `Stufe ${Number(ship.level || 1)} · Dein Expeditionsschiff` : 'Baue dein erstes Floß in der Werft.'}</p>
        <div className="pfp-expedition-ship-bonuses">
          {hasShip ? bonusLabels.map((label) => <span key={label}>☘️ {label}</span>) : <span>⚓ Starte in der Werft mit deinem ersten Schiff.</span>}
        </div>
        {hasShip && nextName ? (
          <div className="pfp-expedition-ship-progress">
            <div><span style={{ width: `${progress}%` }} /></div>
            <small>{progress}% bis {nextName}</small>
          </div>
        ) : (
          <p className="pfp-expedition-ship-note">Mit einem Schiff steigen deine Chancen auf Werft-Ressourcen bei Expeditionen.</p>
        )}
      </div>
      <div className="pfp-expedition-ship-action">
        <p>Schiff verbessert Ressourcenfunde.</p>
        <a href="#/freibeuterhafen/werft" className="pfp-expedition-ship-button">⚒️ Zur Werft</a>
      </div>
    </section>
  );
}

function ExpeditionMap({
  tiles = [],
  viewportTiles = [],
  viewportLoading = false,
  viewportError = '',
  currentPosition,
  lastPosition,
  discoveredCount = 0,
  onLocate,
  onViewportChange,
  shipyard,
  trackingStatus = 'idle',
  trackingAccuracy = null,
  currentTileStatus = null,
}) {
  const discoveredTiles = useMemo(
    () => mergeExpeditionTiles(tiles, viewportTiles),
    [tiles, viewportTiles]
  );
  const discoveredByKey = useMemo(() => {
    const map = new Map();
    discoveredTiles.forEach((tile) => {
      if (tile?.tile_key) {
        map.set(tile.tile_key, tile);
      }
    });
    if (currentTileStatus?.discovered && currentTileStatus?.tile_key) {
      map.set(currentTileStatus.tile_key, {
        ...(currentTileStatus.tile || {}),
        tile_key: currentTileStatus.tile_key,
        visits: Number(currentTileStatus.visits || currentTileStatus.tile?.visits || 1),
      });
    }
    return map;
  }, [discoveredTiles, currentTileStatus?.discovered, currentTileStatus?.tile, currentTileStatus?.tile_key, currentTileStatus?.visits]);

  const fallbackTile = discoveredTiles[0];
  const center = hasCoords(currentPosition)
    ? currentPosition
    : hasCoords(lastPosition)
      ? lastPosition
      : hasCoords(fallbackTile?.center)
        ? fallbackTile.center
        : { latitude: 51.1657, longitude: 10.4515 };

  const currentTileKey = expeditionTileKey(center.latitude, center.longitude);
  const [visibleTileKeys, setVisibleTileKeys] = useState(() => nearbyTileKeys(center, 6));
  const [showHiddenGrid, setShowHiddenGrid] = useState(true);
  const [mapZoom, setMapZoom] = useState(15);
  const ship = shipyard?.ship || null;
  const shipMarkerImage = getShipMarkerImage(ship);
  const shipMarkerLabel = Number(ship?.level || 0) > 0 && String(ship?.name || '').trim()
    ? ship.name
    : 'Dein Standort';
  const shipMarkerIcon = useMemo(() => {
    return divIcon({
      className: 'pfp-expedition-ship-map-marker',
      html: `<span><img src="${escapeMarkerAttribute(shipMarkerImage)}" alt="" /></span>`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      tooltipAnchor: [0, -24],
    });
  }, [shipMarkerImage]);

  const overviewTiles = useMemo(() => {
    if (showHiddenGrid) return [];
    return Array.from(discoveredByKey.values()).filter((tile) => tileBoundsFromPayload(tile));
  }, [discoveredByKey, showHiddenGrid]);

  const handleViewportChange = useCallback((viewport) => {
    const keys = Array.isArray(viewport?.tileKeys) && viewport.tileKeys.length
      ? viewport.tileKeys
      : nearbyTileKeys(center, 6);

    setVisibleTileKeys(keys);
    setShowHiddenGrid(Boolean(viewport?.showHiddenGrid));
    setMapZoom(Number(viewport?.zoom || 15));

    if (typeof onViewportChange === 'function') {
      onViewportChange(viewport);
    }
  }, [center.latitude, center.longitude, onViewportChange]);

  useEffect(() => {
    setVisibleTileKeys(nearbyTileKeys(center, 6));
  }, [currentTileKey]);

  return (
    <section className="pfp-expedition-card pfp-expedition-map-card">
      <div className="pfp-expedition-section-head">
        <div>
          <h2>Expeditionskarte</h2>
          <p>{showHiddenGrid ? 'Helle Kacheln hast du bereits erkundet. Dunkle Felder sind noch unentdeckt.' : 'Uebersicht: erkundete Gebiete werden geladen, Detailraster erscheint beim Hineinzoomen.'}</p>
          <div className={`pfp-expedition-live-status is-${trackingStatus || 'idle'}`}>
            <span>{trackingLabel(trackingStatus)}</span>
            {Number(trackingAccuracy || 0) > 0 ? <small>ca. {Math.round(Number(trackingAccuracy))} m</small> : null}
          </div>
        </div>
        <button type="button" className="pfp-expedition-light-button" onClick={onLocate}>
          Jetzt orten
        </button>
      </div>

      <div className="pfp-expedition-map">
        {viewportLoading ? <div className="pfp-expedition-map-badge">Kartenausschnitt wird geladen</div> : null}
        {viewportError ? <div className="pfp-expedition-map-badge is-error">{viewportError}</div> : null}
        {!showHiddenGrid && !viewportLoading && !viewportError ? <div className="pfp-expedition-map-badge">Uebersicht Zoom {Math.round(mapZoom)}</div> : null}
        <MapContainer
          center={[center.latitude, center.longitude]}
          zoom={15}
          minZoom={EXPEDITION_MAP_MIN_ZOOM}
          maxZoom={18}
          scrollWheelZoom
          zoomControl
          attributionControl={false}
          className="pfp-expedition-map__leaflet"
        >
          <MapViewportSync center={center} />
          <MapGridSync center={center} onViewportChange={handleViewportChange} />
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap"
          />

          {showHiddenGrid ? visibleTileKeys.map((tileKey) => {
            const bounds = tileBoundsFromKey(tileKey);
            const discoveredTile = discoveredByKey.get(tileKey);
            if (!bounds) return null;

            if (discoveredTile) {
              return (
                <Rectangle
                  key={tileKey}
                  bounds={bounds}
                  pathOptions={{
                    color: tileKey === currentTileKey ? '#f59e0b' : '#16a34a',
                    weight: tileKey === currentTileKey ? 3 : 2,
                    fillColor: '#ffffff',
                    fillOpacity: 0.02,
                  }}
                >
                  <Tooltip direction="top">
                    {tileKey === currentTileKey ? 'Aktuelles Gebiet' : 'Erkundetes Gebiet'} · {Number(discoveredTile.visits || 1)} Besuch{Number(discoveredTile.visits || 1) === 1 ? '' : 'e'}
                  </Tooltip>
                </Rectangle>
              );
            }

            return (
              <Rectangle
                key={tileKey}
                bounds={bounds}
                pathOptions={{
                  color: 'rgba(15, 23, 42, 0.5)',
                  weight: 1,
                  fillColor: '#050816',
                  fillOpacity: 0.78,
                }}
              />
            );
          }) : null}

          {!showHiddenGrid ? overviewTiles.map((tile) => {
            const bounds = tileBoundsFromPayload(tile);
            if (!bounds) return null;

            return (
              <Rectangle
                key={tile.tile_key}
                bounds={bounds}
                pathOptions={{
                  color: tile.tile_key === currentTileKey ? '#f59e0b' : '#16a34a',
                  weight: tile.tile_key === currentTileKey ? 3 : 2,
                  fillColor: '#ffffff',
                  fillOpacity: 0.04,
                }}
              >
                <Tooltip direction="top">
                  {tile.tile_key === currentTileKey ? 'Aktuelles Gebiet' : 'Erkundetes Gebiet'} Â· {Number(tile.visits || 1)} Besuch{Number(tile.visits || 1) === 1 ? '' : 'e'}
                </Tooltip>
              </Rectangle>
            );
          }) : null}

          {hasCoords(center) ? (
            <Marker position={[center.latitude, center.longitude]} icon={shipMarkerIcon}>
              <Tooltip direction="top">{shipMarkerLabel}</Tooltip>
            </Marker>
          ) : null}
        </MapContainer>
      </div>

      <div className="pfp-expedition-map-legend">
        <span><i className="is-current" /> aktuelles Gebiet</span>
        <span><i className="is-discovered" /> erkundet</span>
        <span><i className="is-hidden" /> unentdeckt</span>
        <strong>{Number(discoveredCount || discoveredTiles.length)} Gebiete</strong>
      </div>
    </section>
  );
}

function RecentLog({ recent = [] }) {
  if (!Array.isArray(recent) || !recent.length) {
    return (
      <div className="pfp-expedition-empty">
        Noch keine Expeditionen. Starte den Scanner, wenn du draußen unterwegs bist.
      </div>
    );
  }

  return (
    <div className="pfp-expedition-log-list">
      {recent.map((item) => (
        <div key={item.id} className={`pfp-expedition-log-item rarity-${item.rarity || 'common'}`}>
          <div className="pfp-expedition-log-main">
            <div className="pfp-expedition-log-thumb">
              <LootThumb imageUrl={item.image_url || item.imageUrl} icon={item.icon || '🎁'} label={item.loot_label || 'Expeditionsfund'} />
            </div>
            <div>
              <strong>{item.loot_label || 'Expeditionsfund'}</strong>
              <span>{formatDateTime(item.created_at)} · {rarityLabel(item.rarity)}</span>
            </div>
          </div>
          <div className="pfp-expedition-log-reward">
            {Number(item.xp_awarded || 0) > 0 ? <span>+{Number(item.xp_awarded)} XP</span> : null}
            {Number(item.coins_awarded || 0) > 0 ? <span>+{formatCoinReward(item.coins_awarded)}</span> : null}
            {Number(item.fragments_awarded || 0) > 0 ? <span>+{Number(item.fragments_awarded)} 🗺️</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ExpeditionsPage() {
  const gamification = useGamification() || {};
  const playerLocation = usePlayerLocation();
  const { applyGamificationPayload } = gamification;
  const sessionContext = useOptionalAppSession();
  const mergeSessionPatch = sessionContext?.mergeSessionPatch;
  const bootstrapExpeditions = sessionContext?.session?.expeditions || null;
  const bootstrapShipyard = sessionContext?.session?.shipyard || null;
  const hasBootstrapExpeditions = Boolean(bootstrapExpeditions);

  const [data, setData] = useState(() => bootstrapExpeditions);
  const [loading, setLoading] = useState(!hasBootstrapExpeditions);
  const [scanning, setScanning] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const [shipyard, setShipyard] = useState(() => bootstrapShipyard);
  const [cooldownNow, setCooldownNow] = useState(() => Date.now());
  const [currentPosition, setCurrentPosition] = useState(null);
  const [trackingStatus, setTrackingStatus] = useState('idle');
  const [trackingAccuracy, setTrackingAccuracy] = useState(null);
  const [currentTileStatus, setCurrentTileStatus] = useState(null);
  const [expeditionViewport, setExpeditionViewport] = useState(null);
  const [viewportTiles, setViewportTiles] = useState([]);
  const [viewportTilesLoading, setViewportTilesLoading] = useState(false);
  const [viewportTilesError, setViewportTilesError] = useState('');
  const [dismissedTilePromptKey, setDismissedTilePromptKey] = useState('');
  const lastLivePositionRef = useRef(null);
  const loadedViewportKeyRef = useRef('');

  const state = data?.state || {};
  const config = data?.config || {};
  const recent = data?.recent || [];
  const lastKnownPosition = state.last_position || null;

  const cooldownLeft = getCooldownLeftSeconds(state, cooldownNow);
  const availableScans = Number(state.available_scans || 0);
  const stepInterval = Number(state.scanner_step_interval || config.step_energy_interval || 100);
  const stepProgress = Number(state.scanner_step_progress || 0);
  const stepsRemaining = Number(state.scanner_steps_remaining || stepInterval);
  const canScan = !loading && !scanning && availableScans > 0 && cooldownLeft <= 0;
  const discoveredTileLookup = useMemo(() => {
    const lookup = new Map();
    (Array.isArray(state.discovered_tiles_map) ? state.discovered_tiles_map : []).forEach((tile) => {
      if (tile?.tile_key) lookup.set(tile.tile_key, tile);
    });
    (Array.isArray(viewportTiles) ? viewportTiles : []).forEach((tile) => {
      if (tile?.tile_key) lookup.set(tile.tile_key, tile);
    });
    return lookup;
  }, [state.discovered_tiles_map, viewportTiles]);
  const discoveredTileKeys = useMemo(() => new Set(discoveredTileLookup.keys()), [discoveredTileLookup]);
  const currentTileKey = hasCoords(currentPosition)
    ? expeditionTileKey(currentPosition.latitude, currentPosition.longitude)
    : '';
  const currentTileConfirmedUndiscovered = Boolean(
    currentTileKey
      && currentTileStatus?.tile_key === currentTileKey
      && currentTileStatus.discovered === false
  );
  const showLiveExpeditionPrompt = Boolean(
    currentTileConfirmedUndiscovered
      && canScan
      && dismissedTilePromptKey !== currentTileKey
  );

  const scanButtonLabel = useMemo(() => {
    if (scanning) return 'Scanner läuft …';
    if (cooldownLeft > 0) return `Scanner lädt noch ${cooldownLeft}s`;
    if (availableScans <= 0) return 'Keine Scannerladung übrig';
    return 'Umgebung scannen';
  }, [availableScans, cooldownLeft, scanning]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await loadExpeditionState();
      setData(result || null);
      if (mergeSessionPatch) {
        mergeSessionPatch({ expeditions: result || null });
      }
    } catch (err) {
      setError(err.message || 'Expeditionen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [mergeSessionPatch]);

  useEffect(() => {
    if (hasBootstrapExpeditions) {
      setData(bootstrapExpeditions);
      setLoading(false);
      return;
    }

    load();
  }, [bootstrapExpeditions, hasBootstrapExpeditions, load]);

  const handleExpeditionViewportChange = useCallback((viewport) => {
    const key = viewportCacheKey(viewport);
    if (!key) return;

    setExpeditionViewport((previous) => (
      previous?.key === key ? previous : { ...viewport, key }
    ));
  }, []);

  useEffect(() => {
    if (!expeditionViewport?.bbox || !expeditionViewport?.key) {
      return undefined;
    }

    if (loadedViewportKeyRef.current === expeditionViewport.key) {
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setViewportTilesLoading(true);
      setViewportTilesError('');

      loadExpeditionTiles({
        bbox: expeditionViewport.bbox,
        limit: EXPEDITION_VIEWPORT_TILE_LIMIT,
      })
        .then((result) => {
          if (cancelled) return;
          setViewportTiles(Array.isArray(result?.tiles) ? result.tiles : []);
          loadedViewportKeyRef.current = expeditionViewport.key;
        })
        .catch(() => {
          if (!cancelled) {
            setViewportTilesError('Ausschnitt konnte nicht geladen werden');
          }
        })
        .finally(() => {
          if (!cancelled) {
            setViewportTilesLoading(false);
          }
        });
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [expeditionViewport]);

  useEffect(() => {
    const sharedPosition = playerLocation.position;
    if (!sharedPosition) {
      setTrackingStatus(playerLocation.status === 'denied' ? 'blocked' : playerLocation.status === 'requesting' ? 'pending' : 'unavailable');
      return;
    }

    const nextPosition = { ...sharedPosition, available: true };
    const previousPosition = lastLivePositionRef.current;
    const previousTile = expeditionTileKey(previousPosition?.latitude, previousPosition?.longitude);
    const nextTile = expeditionTileKey(nextPosition.latitude, nextPosition.longitude);
    const movedMeters = distanceMeters(previousPosition, nextPosition);

    setTrackingStatus('watching');
    setTrackingAccuracy(nextPosition.accuracy);
    if (previousPosition && previousTile === nextTile && movedMeters !== null && movedMeters < LIVE_POSITION_MIN_DISTANCE_METERS) return;

    lastLivePositionRef.current = nextPosition;
    setCurrentPosition(nextPosition);
  }, [playerLocation.position, playerLocation.status]);

  useEffect(() => {
    if (bootstrapShipyard) {
      setShipyard(bootstrapShipyard);
      return undefined;
    }

    let cancelled = false;
    loadShipyard()
      .then((result) => {
        if (!cancelled) {
          setShipyard(result || null);
          if (mergeSessionPatch) {
            mergeSessionPatch({ shipyard: result || null });
          }
        }
      })
      .catch(() => { if (!cancelled) setShipyard(null); });
    return () => { cancelled = true; };
  }, [bootstrapShipyard, mergeSessionPatch]);

  useEffect(() => {
    function handleStepCharge(event) {
      const added = Number(event?.detail?.scanner_energy_added || 0);
      if (added > 0) {
        setMessage(`Scannerladung +${added} durch Schritte erhalten`);
        load();
      }
    }

    function handlePfandCharge(event) {
      const detail = event?.detail || {};
      const added = Number(detail.scanner_energy_added || 0);

      if (added > 0) {
        setMessage(`Scannerladung +${added} durch Pfandeintrag erhalten`);
        if (detail.state) {
          setData((prev) => ({
            ...(prev || {}),
            state: {
              ...(prev?.state || {}),
              ...detail.state,
            },
          }));
        }
        load();
      }
    }

    window.addEventListener('pfandpirat:expedition-step-charge', handleStepCharge);
    window.addEventListener('pfandpirat:expedition-pfand-charge', handlePfandCharge);
    return () => {
      window.removeEventListener('pfandpirat:expedition-step-charge', handleStepCharge);
      window.removeEventListener('pfandpirat:expedition-pfand-charge', handlePfandCharge);
    };
  }, [load]);

  useEffect(() => {
    if (!cooldownLeft) return undefined;
    const timer = window.setInterval(() => {
      setCooldownNow(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownLeft]);

  useEffect(() => {
    if (!currentTileKey || !hasCoords(currentPosition)) {
      setCurrentTileStatus(null);
      return undefined;
    }

    if (discoveredTileKeys.has(currentTileKey)) {
      const tile = discoveredTileLookup.get(currentTileKey) || null;
      setCurrentTileStatus({
        tile_key: currentTileKey,
        discovered: true,
        tile,
        visits: Number(tile?.visits || 1),
      });
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      getExpeditionTileStatus(currentPosition)
        .then((result) => {
          if (cancelled) return;
          if (result?.state || result?.config) {
            setData((prev) => ({
              ...(prev || {}),
              config: result?.config || prev?.config || {},
              state: {
                ...(prev?.state || {}),
                ...(result?.state || {}),
              },
            }));
          }
          if (result?.tile) {
            setViewportTiles((previous) => mergeExpeditionTiles(previous, [result.tile]));
          }
          setCurrentTileStatus({
            tile_key: result?.tile_key || currentTileKey,
            discovered: Boolean(result?.discovered),
            tile: result?.tile || null,
            visits: Number(result?.tile?.visits || 0),
          });
        })
        .catch(() => {
          if (!cancelled) {
            setCurrentTileStatus(null);
          }
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [currentPosition, currentTileKey, discoveredTileKeys, discoveredTileLookup]);

  const handleLocate = useCallback(async () => {
    const position = playerLocation.position || await playerLocation.requestConsent();
    if (position) {
      const nextPosition = { ...position, available: true };
      setCurrentPosition(nextPosition);
      lastLivePositionRef.current = nextPosition;
      setTrackingStatus('watching');
      setTrackingAccuracy(nextPosition.accuracy);
      setMessage('Karte auf deinen aktuellen Standort zentriert.');
      return;
    }

    setMessage('Standort konnte nicht ermittelt werden. Die Karte zeigt den letzten bekannten Expeditionspunkt.');
  }, [playerLocation]);

  async function handleScan() {
    if (!canScan) return;

    setScanning(true);
    setError('');
    setMessage('Scanner wird kalibriert …');
    setLastResult(null);

    try {
      const position = playerLocation.position || await playerLocation.requestConsent();
      const livePosition = hasCoords(currentPosition)
        ? { ...currentPosition, available: true }
        : null;
      const scanPosition = position ? { ...position, available: true } : (livePosition || { latitude: null, longitude: null, accuracy: null, available: false });

      if (scanPosition.available) {
        setCurrentPosition(scanPosition);
        lastLivePositionRef.current = scanPosition;
        setTrackingStatus('watching');
        setTrackingAccuracy(scanPosition.accuracy);
      }
      setMessage(scanPosition.available ? 'Umgebung wird gescannt …' : 'Scan ohne genaue Position wird ausgeführt …');

      const result = await scanExpeditionArea(scanPosition);
      setData(result || null);
      setViewportTiles((previous) => mergeExpeditionTiles(previous, result?.state?.discovered_tiles_map || []));
      loadedViewportKeyRef.current = '';
      if (result?.session_patch && mergeSessionPatch) {
        mergeSessionPatch(result.session_patch);
      } else if (mergeSessionPatch) {
        mergeSessionPatch({ expeditions: result || null });
      }

      setLastResult(result || null);
      const scannedTileKey = expeditionTileKey(scanPosition.latitude, scanPosition.longitude);
      if (scannedTileKey) {
        const scannedTile = (result?.state?.discovered_tiles_map || []).find((tile) => tile?.tile_key === scannedTileKey) || null;
        setCurrentTileStatus({
          tile_key: scannedTileKey,
          discovered: true,
          tile: scannedTile,
          visits: Number(scannedTile?.visits || 1),
        });
        setDismissedTilePromptKey(scannedTileKey);
      }

      if (typeof applyGamificationPayload === 'function') {
        applyGamificationPayload(result, {
          emitXpToast: true,
          event: result?.event || 'expedition_scan',
          awarded: Number(result?.awarded || 0),
          toastLabel: result?.xp_toast?.label || 'Expedition abgeschlossen',
        });
      }

      const lootLabel = result?.loot?.label || 'Fund entdeckt';
      const extras = [];
      if (result?.new_tile_discovered) extras.push('neues Gebiet');
      if (result?.map_complete) extras.push('Schatzkarte komplett');
      const resourceText = formatResourceRewards(result?.resources_awarded || []);
      if (resourceText) extras.push(resourceText);
      setMessage(extras.length ? `${lootLabel} · ${extras.join(' · ')}` : lootLabel);
      if (resourceText) {
        loadShipyard()
          .then((shipyardResult) => {
            setShipyard(shipyardResult || null);
            if (mergeSessionPatch) {
              mergeSessionPatch({ shipyard: shipyardResult || null });
            }
          })
          .catch(() => {});
      }
    } catch (err) {
      const payloadState = err?.payload?.data?.state || err?.payload?.state;
      if (payloadState) {
        setData((prev) => ({ ...(prev || {}), ...(payloadState.config ? payloadState : {}), state: payloadState.state || payloadState }));
      }
      setError(err.message || 'Der Scan konnte nicht ausgeführt werden.');
      setMessage('');
    } finally {
      window.setTimeout(() => setScanning(false), 450);
    }
  }

  return (
    <main className="pfp-expedition-page">
      <section className="pfp-expedition-hero">
        <div className="pfp-expedition-hero-content">
          <span className="pfp-expedition-kicker">Neue Spielschicht</span>
          <h1>🧭 Pfandpirat Expeditionen</h1>
          <p>
            Starte virtuelle Expeditionen, entdecke Treibgut, Schatzkisten und Kartenfragmente. Zusätzliche Scannerladungen entstehen durch Pfandeinträge und Bewegung.
          </p>
        </div>
        <div className={`pfp-expedition-scanner-orb ${scanning ? 'is-scanning' : ''}`} aria-hidden="true">
          <span>🧭</span>
        </div>
      </section>

      {error ? <div className="pfp-expedition-alert pfp-expedition-alert-error">{error}</div> : null}
      {message ? <div className="pfp-expedition-alert">{message}</div> : null}
      <ExpeditionStartPrompt
        visible={showLiveExpeditionPrompt}
        accuracy={currentPosition?.accuracy}
        availableScans={availableScans}
        disabled={!canScan}
        onStart={() => {
          setDismissedTilePromptKey(currentTileKey);
          handleScan();
        }}
        onDismiss={() => setDismissedTilePromptKey(currentTileKey)}
      />

      <ExpeditionMap
        tiles={state.discovered_tiles_map || []}
        viewportTiles={viewportTiles}
        viewportLoading={viewportTilesLoading}
        viewportError={viewportTilesError}
        currentPosition={currentPosition}
        lastPosition={lastKnownPosition}
        discoveredCount={state.discovered_tiles_count}
        onLocate={handleLocate}
        onViewportChange={handleExpeditionViewportChange}
        shipyard={shipyard}
        trackingStatus={trackingStatus}
        trackingAccuracy={trackingAccuracy}
        currentTileStatus={currentTileStatus}
      />

      <section className="pfp-expedition-card pfp-expedition-scan-card">
        <div>
          <h2>Scanner</h2>
          <p>
            Du hast täglich {Number(state.daily_free_scans || config.daily_free_scans || 3)} freie Scans. Weitere Ladungen entstehen durch echte Pfandeinträge oder jeweils {stepInterval} weitere Schritte.
          </p>
        </div>
        <button className="pfp-expedition-scan-button" type="button" disabled={!canScan} onClick={handleScan}>
          <span>{scanning ? '🌊' : '🧭'}</span>
          {scanButtonLabel}
        </button>
      </section>

      <ShipCard shipyard={shipyard} />

      <section className="pfp-expedition-stats-grid">
        <StatCard
          icon="🔎"
          label="Heute verfügbar"
          value={loading ? '…' : availableScans}
          hint={`${Number(state.free_scans_remaining || 0)} frei · ${Number(state.scanner_energy || 0)} Energie · ${Math.max(0, stepsRemaining)} Schritte bis +1`}
        />
        <StatCard
          icon="🗺️"
          label="Kartenfragmente"
          value={`${Number(state.map_fragments || 0)}/${Number(state.map_fragments_needed || config.map_fragments_needed || 6)}`}
          hint="6 Fragmente ergeben eine Schatzkarte"
        />
        <StatCard
          icon="📍"
          label="Gebiete erkundet"
          value={loading ? '…' : Number(state.discovered_tiles_count || 0)}
          hint="neue Orte geben Bonus-XP"
        />
        <StatCard
          icon="🏴‍☠️"
          label="Schatzkarten"
          value={loading ? '…' : Number(state.completed_maps || 0)}
          hint="Langzeitfortschritt"
        />
      </section>

      <section className="pfp-expedition-card">
        <div className="pfp-expedition-section-head">
          <div>
            <h2>Schatzkarten-Fortschritt</h2>
            <p>Sammle Fragmente durch Expeditionen und seltene Funde.</p>
          </div>
          <strong>{Number(state.map_fragments || 0)}/{Number(state.map_fragments_needed || config.map_fragments_needed || 6)}</strong>
        </div>
        <ProgressBar value={Number(state.map_fragments || 0)} max={Number(state.map_fragments_needed || config.map_fragments_needed || 6)} />
      </section>

      {lastResult?.loot ? (
        <section className={`pfp-expedition-result-card rarity-${lastResult.loot.rarity || 'common'}`}>
          <div className="pfp-expedition-result-icon">
            <LootThumb
              imageUrl={lastResult.loot.image_url || lastResult.loot.imageUrl}
              icon={lastResult.loot.icon || '🧭'}
              label={lastResult.loot.label || 'Expeditionsfund'}
            />
          </div>
          <div>
            <span>{rarityLabel(lastResult.loot.rarity)}</span>
            <h2>{lastResult.loot.label}</h2>
            <p>{lastResult.loot.text}</p>
            <div className="pfp-expedition-result-rewards">
              {Number(lastResult.awarded || 0) > 0 ? <strong>+{Number(lastResult.awarded)} XP</strong> : null}
              {Number(lastResult.coins_awarded || 0) > 0 ? <strong>+{formatCoinReward(lastResult.coins_awarded)}</strong> : null}
              {Number(lastResult.fragments_awarded || 0) > 0 ? <strong>+{Number(lastResult.fragments_awarded)} Fragment</strong> : null}
              {Array.isArray(lastResult.resources_awarded) ? lastResult.resources_awarded.map((resource) => (
                <strong key={resource.slug || resource.name}>+{Number(resource.quantity || 1)} {resource.name || 'Werft-Ressource'}</strong>
              )) : null}
            </div>
          </div>
        </section>
      ) : null}

      <section className="pfp-expedition-card">
        <div className="pfp-expedition-section-head">
          <div>
            <h2>Letzte Expeditionen</h2>
            <p>Deine letzten virtuellen Funde.</p>
          </div>
          <button type="button" className="pfp-expedition-light-button" onClick={load}>Aktualisieren</button>
        </div>
        <RecentLog recent={recent} />
      </section>
    </main>
  );
}
